import { useTranslation } from 'react-i18next';

function Ic({ type, size = 20, color = 'var(--soleil-gold)' }: { type: string; size?: number; color?: string }) {
  const p = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: color, strokeWidth: '2', strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  const icons: Record<string, React.ReactNode> = {
    grad: <svg {...p}><path d="M22 10v6M2 10l10-5 10 5-10 5z" /><path d="M6 12v5c0 2 3 3 6 3s6-1 6-3v-5" /></svg>,
    users: <svg {...p}><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 00-3-3.87" /><path d="M16 3.13a4 4 0 010 7.75" /></svg>,
    book: <svg {...p}><path d="M4 19.5A2.5 2.5 0 016.5 17H20" /><path d="M4 4.5A2.5 2.5 0 016.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15z" /></svg>,
    target: <svg {...p}><circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" /></svg>,
    shield: <svg {...p}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>,
    sun: <svg {...p}><circle cx="12" cy="12" r="5" /><line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" /><line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" /><line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" /><line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" /></svg>,
  };
  return <>{icons[type] || null}</>;
}

function IcBox({ type, color, bg, size = 52 }: { type: string; color: string; bg: string; size?: number }) {
  return (
    <div style={{ width: size, height: size, borderRadius: size * 0.26, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Ic type={type} size={size * 0.48} color={color} />
    </div>
  );
}

const S = {
  font: "'Sora', system-ui, sans-serif",
  heading: "'Fraunces', Georgia, serif",
};

export default function SoleilAboutPage() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  return (
    <div>
      {/* Hero */}
      <section style={{ padding: '72px 24px 56px', background: 'var(--soleil-cream)', textAlign: 'center' }}>
        <div style={{ maxWidth: 640, margin: '0 auto' }}>
          <div style={{ fontFamily: S.font, fontSize: 11, fontWeight: 700, color: 'var(--soleil-sienna)', letterSpacing: '0.2em', marginBottom: 14 }}>
            {isRTL ? 'من نحن' : 'ABOUT US'}
          </div>
          <h1 style={{ fontFamily: S.heading, fontSize: 'clamp(32px, 4vw, 48px)', fontWeight: 600, color: 'var(--soleil-ink)', lineHeight: 1.1, marginBottom: 20 }}>
            {isRTL ? 'نُضيء مسيرتك ' : 'Illuminating '}
            <span style={{ color: 'var(--soleil-gold)', fontStyle: 'italic' }}>
              {isRTL ? 'المهنية' : 'careers'}
            </span>
            {isRTL ? '' : ' in IT'}
          </h1>
          <p style={{ fontFamily: S.font, fontSize: 16, color: 'var(--soleil-sub)', lineHeight: 1.8 }}>
            {t('about.subtitle')}
          </p>
        </div>
      </section>

      {/* Mission */}
      <section style={{ padding: '56px 24px', background: 'var(--soleil-cream)', borderTop: '1px solid var(--soleil-sand)' }}>
        <div style={{ maxWidth: 700, margin: '0 auto', textAlign: 'center' }}>
          <Ic type="sun" size={28} color="var(--soleil-gold)" />
          <h2 style={{ fontFamily: S.heading, fontSize: 28, fontWeight: 600, color: 'var(--soleil-ink)', margin: '16px 0 12px' }}>
            {isRTL ? 'مهمتنا' : 'Our Mission'}
          </h2>
          <p style={{ fontFamily: S.font, fontSize: 15, color: 'var(--soleil-sub)', lineHeight: 1.8 }}>
            {t('about.description')}
          </p>
        </div>
      </section>

      {/* Features */}
      <section style={{ padding: '72px 24px', background: 'var(--soleil-cream)', borderTop: '1px solid var(--soleil-sand)' }}>
        <div style={{ maxWidth: 1060, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <h2 style={{ fontFamily: S.heading, fontSize: 'clamp(24px, 3vw, 36px)', fontWeight: 600, color: 'var(--soleil-ink)' }}>
              {isRTL ? 'ما يميزنا ' : 'What sets us '}
              <span style={{ color: 'var(--soleil-gold)', fontStyle: 'italic' }}>
                {isRTL ? '' : 'apart'}
              </span>
            </h2>
          </div>
          <div className="soleil-grid-features" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
            {([
              ['grad', t('about.features.certified.title'), t('about.features.certified.description'), 'var(--soleil-gold)', 'var(--soleil-gold-pale)'],
              ['users', t('about.features.expert.title'), t('about.features.expert.description'), 'var(--soleil-sienna)', 'var(--soleil-sienna-pale)'],
              ['book', t('about.features.comprehensive.title'), t('about.features.comprehensive.description'), 'var(--soleil-olive)', 'var(--soleil-olive-pale)'],
              ['target', t('about.features.practical.title'), t('about.features.practical.description'), 'var(--soleil-gold-dark)', 'var(--soleil-gold-pale)'],
            ] as [string, string, string, string, string][]).map(([ic, title, desc, color, bg], i) => (
              <div key={i} style={{ background: '#fff', borderRadius: 18, border: '1px solid var(--soleil-sand)', padding: '32px 24px', position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: color, opacity: 0.65 }} />
                <IcBox type={ic} color={color} bg={bg} />
                <h3 style={{ fontFamily: S.font, fontSize: 16, fontWeight: 700, color: 'var(--soleil-ink)', margin: '20px 0 8px' }}>{title}</h3>
                <p style={{ fontFamily: S.font, fontSize: 13.5, color: 'var(--soleil-sub)', lineHeight: 1.7, margin: 0 }}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section style={{ padding: '72px 24px', background: 'var(--soleil-linen)', borderTop: '1px solid var(--soleil-sand)' }}>
        <div style={{ maxWidth: 740, margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ fontFamily: S.heading, fontSize: 32, fontWeight: 600, color: 'var(--soleil-ink)', marginBottom: 40 }}>
            {isRTL ? 'قيمنا ' : 'Our '}
            <span style={{ color: 'var(--soleil-gold)', fontStyle: 'italic' }}>
              {isRTL ? 'الأساسية' : 'values'}
            </span>
          </h2>
          <div className="soleil-grid-values" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }}>
            {([
              ['shield', isRTL ? 'الجودة' : 'Quality', isRTL ? 'لا تنازل عن جودة المحتوى' : 'No compromise on content quality'],
              ['users', isRTL ? 'المجتمع' : 'Community', isRTL ? 'بناء مجتمع مهني قوي' : 'Building a strong professional community'],
              ['target', isRTL ? 'النتائج' : 'Results', isRTL ? 'نجاحكم هو نجاحنا' : 'Your success is our success'],
            ] as [string, string, string][]).map(([ic, title, desc], i) => (
              <div key={i} style={{ padding: '24px 16px' }}>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
                  <Ic type={ic} size={28} color="var(--soleil-gold)" />
                </div>
                <h3 style={{ fontFamily: S.font, fontSize: 15, fontWeight: 700, color: 'var(--soleil-ink)', marginBottom: 6 }}>{title}</h3>
                <p style={{ fontFamily: S.font, fontSize: 13, color: 'var(--soleil-sub)', lineHeight: 1.6 }}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Official Accreditation */}
      <section style={{ padding: '64px 24px', background: 'var(--soleil-cream)', borderTop: '1px solid var(--soleil-sand)' }}>
        <div style={{ maxWidth: 680, margin: '0 auto', textAlign: 'center' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
            <Ic type="award" size={32} color="var(--soleil-gold)" />
          </div>
          <h2 style={{ fontFamily: S.heading, fontSize: 28, fontWeight: 600, color: 'var(--soleil-ink)', marginBottom: 12 }}>
            {isRTL ? 'الاعتماد ' : 'Official '}
            <span style={{ color: 'var(--soleil-gold)', fontStyle: 'italic' }}>
              {isRTL ? 'الرسمي' : 'Accreditation'}
            </span>
          </h2>
          <p style={{ fontFamily: S.font, fontSize: 15, color: 'var(--soleil-sub)', lineHeight: 1.8, marginBottom: 20 }}>
            {isRTL
              ? 'يتم تقديم هذا التدريب بالشراكة مع Raj Khanna Associates Limited، وهي منظمة تدريب معتمدة (ATO) من PeopleCert ومقرها المملكة المتحدة.'
              : 'This training is provided in partnership with Raj Khanna Associates Limited, a United Kingdom–based Accredited Training Organisation (ATO) of PeopleCert.'}
          </p>
          <a
          
            href="https://www.peoplecert.org"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontFamily: S.font, fontSize: 13, fontWeight: 600,
              color: 'var(--soleil-gold-dark)', textDecoration: 'none',
              padding: '10px 24px', borderRadius: 8,
              border: '1px solid var(--soleil-gold)',
              display: 'inline-block',
              transition: 'all 0.2s',
            }}
          >
            {isRTL ? 'تحقق على PeopleCert 2190' : 'Verify on PeopleCert 2192'}
          </a>
        </div>
      </section>
    </div>
  );
}
